import { useState } from 'react';
import type React from 'react';
import type { UseMutationResult } from '@tanstack/react-query';

import Button from 'components/partials/button/button';
import Modal from 'components/partials/modal/modal';

import { useCurrentUser } from 'hooks/api/use-current-user';
import type {
  TransitionClientUsersArgs,
  TransitionDirection,
} from 'hooks/api/use-clients';
import type { UserFederationResultDto } from 'api/generated/schemas';

import TransitionConfirmStep from './transition-confirm-step';
import TransitionResultStep from './transition-result-step';
import { TRANSITION_COPY } from './transition-users-modal.constants';

interface TransitionUsersModalProps {
  direction: TransitionDirection;
  clientId: number;
  mutation: UseMutationResult<
    UserFederationResultDto,
    Error,
    TransitionClientUsersArgs
  >;
  onCloseCallback: () => void;
}

type Step = 'confirm' | 'result';

const TransitionUsersModal = ({
  direction,
  clientId,
  mutation,
  onCloseCallback,
}: TransitionUsersModalProps): React.JSX.Element => {
  const [step, setStep] = useState<Step>('confirm');
  const [result, setResult] = useState<UserFederationResultDto | null>(null);

  const { data: currentUser } = useCurrentUser();
  const copy = TRANSITION_COPY[direction];

  const handleConfirm = async (): Promise<void> => {
    const userId = currentUser?.oktaUserId;

    if (!userId) return;

    try {
      const data = await mutation.mutateAsync({ userId, clientId });

      setResult(data);
      setStep('result');
    } catch {
      onCloseCallback();
    }
  };

  const footer =
    step === 'confirm' ? (
      <div className="flex justify-start gap-x-2">
        <Button
          variant="brand-filled"
          loading={mutation.isPending}
          disabled={mutation.isPending}
          onClick={handleConfirm}
        >
          {copy.confirmLabel}
        </Button>
        <Button
          variant="reserve-outlined"
          onClick={onCloseCallback}
          disabled={mutation.isPending}
        >
          Later
        </Button>
      </div>
    ) : (
      <div className="flex justify-end">
        <Button variant="brand-filled" onClick={onCloseCallback}>
          Done
        </Button>
      </div>
    );

  return (
    <Modal
      headerText={step === 'confirm' ? copy.headerText : 'Transition complete'}
      onCloseCallback={onCloseCallback}
      footer={footer}
      primaryAction={{
        onClick: step === 'confirm' ? handleConfirm : onCloseCallback,
        disabled: mutation.isPending,
        loading: mutation.isPending,
      }}
      disableBackdropClick
      secondaryAction={{
        onClick: onCloseCallback,
        disabled: mutation.isPending,
      }}
    >
      {step === 'confirm' ? (
        <TransitionConfirmStep copy={copy} />
      ) : (
        <TransitionResultStep
          total={result?.total ?? 0}
          succeeded={result?.succeeded ?? 0}
          failures={result?.failures ?? []}
          toTransitionLabel={copy.toTransitionLabel}
        />
      )}
    </Modal>
  );
};

export default TransitionUsersModal;
